/**
 * Y.Doc schema helpers (SPEC §5). All household data lives in one Y.Doc:
 *   items: Y.Map<ItemId, Y.Map>        every item
 *   fields: Y.Map<FieldKey, FieldDef>  field registry
 *   users: Y.Map<UserId, UserInfo>
 *   settings: Y.Map                    household settings
 *   userSettings: Y.Map<UserId, Y.Map> per-user settings
 *
 * Nested Y types are used where concurrent edits must merge:
 *   title: Y.Text, body: Y.XmlFragment, tags: Y.Map<true>, props: Y.Map,
 *   attachments: Y.Map<Attachment>, diagrams: Y.Map<Diagram>, completions: Y.Array<Completion>.
 *
 * Mutating helpers here assume they run inside `doc.transact(...)`.
 */
import * as Y from 'yjs'
import { ulid } from './ids'
import { isValidTimeZone } from './dates'
import { normalizePageOrder } from './page-order'
import { isChartSpec } from './charts'
import type { TreeNodeInput } from './tree'
import type { Diagram } from './diagrams'
import { displayText, type CalcLine } from './math'
import {
  HOUSEHOLD_DEFAULTS,
  USER_DEFAULTS,
  type ActorId,
  type Attachment,
  type Completion,
  type FieldDef,
  type HouseholdSettings,
  type ItemSnapshot,
  type LinkPreview,
  type Recurrence,
  type Reminder,
  type SystemKind,
  type Tint,
  type UserId,
  type UserInfo,
  type UserSettings,
} from './types'

export type YItem = Y.Map<unknown>

export const itemsMap = (doc: Y.Doc) => doc.getMap<YItem>('items')
export const fieldsMap = (doc: Y.Doc) => doc.getMap<FieldDef>('fields')
export const usersMap = (doc: Y.Doc) => doc.getMap<UserInfo>('users')
export const settingsMap = (doc: Y.Doc) => doc.getMap<unknown>('settings')
export const userSettingsMap = (doc: Y.Doc) => doc.getMap<Y.Map<unknown>>('userSettings')

export function getYItem(doc: Y.Doc, id: string): YItem | undefined {
  const y = itemsMap(doc).get(id)
  return y instanceof Y.Map ? y : undefined
}

// ---------------------------------------------------------------------------
// Reading

export function yTitle(y: YItem): Y.Text | undefined {
  const t = y.get('title')
  return t instanceof Y.Text ? t : undefined
}
export function yBody(y: YItem): Y.XmlFragment | undefined {
  const b = y.get('body')
  return b instanceof Y.XmlFragment ? b : undefined
}
/** Take a photo out of a note when the photo itself goes (its attachment removed); how many went. */
export function removeBodyImages(frag: Y.XmlFragment | undefined, attId: string): number {
  let n = 0
  // `att/<itemId>/<attId>.<ext>` (as richtext's attIdFromKey reads it).
  const fromKey = (key: unknown) => (String(key ?? '').split('/')[2] ?? '').split('.')[0]
  const walk = (parent: Y.XmlFragment | Y.XmlElement) => {
    const kids = parent.toArray()
    for (let i = kids.length - 1; i >= 0; i--) {
      const c = kids[i]
      if (!(c instanceof Y.XmlElement)) continue
      if (c.nodeName === 'image' && (c.getAttribute('attId') === attId || fromKey(c.getAttribute('key')) === attId)) {
        parent.delete(i, 1)
        n++
      } else walk(c)
    }
  }
  if (frag) walk(frag)
  return n
}
export function yTags(y: YItem): Y.Map<true> | undefined {
  const t = y.get('tags')
  return t instanceof Y.Map ? (t as Y.Map<true>) : undefined
}
export function yProps(y: YItem): Y.Map<unknown> | undefined {
  const p = y.get('props')
  return p instanceof Y.Map ? p : undefined
}
export function yAttachments(y: YItem): Y.Map<Attachment> | undefined {
  const a = y.get('attachments')
  return a instanceof Y.Map ? (a as Y.Map<Attachment>) : undefined
}
export function yDiagrams(y: YItem): Y.Map<Diagram> | undefined {
  const d = y.get('diagrams')
  return d instanceof Y.Map ? (d as Y.Map<Diagram>) : undefined
}
export function yCompletions(y: YItem): Y.Array<Completion> | undefined {
  const c = y.get('completions')
  return c instanceof Y.Array ? (c as Y.Array<Completion>) : undefined
}

export function readTitle(y: YItem): string {
  return yTitle(y)?.toString() ?? ''
}

const BLOCK_NODES = new Set(['paragraph', 'heading', 'blockquote', 'codeBlock', 'listItem', 'taskItem', 'horizontalRule'])

/** Plain text of a rich body (for search, FTS and previews). */
export function fragmentText(frag: Y.XmlFragment | undefined): string {
  if (!frag || frag.length === 0) return ''
  const out: string[] = []
  const walk = (node: Y.XmlElement | Y.XmlText | Y.XmlHook | Y.XmlFragment) => {
    if (node instanceof Y.XmlText) {
      for (const op of node.toDelta() as Array<{ insert: unknown }>) {
        if (typeof op.insert === 'string') out.push(op.insert)
      }
      return
    }
    if (node instanceof Y.XmlElement) {
      if (node.nodeName === 'wikilink') {
        out.push(String(node.getAttribute('title') ?? ''))
        return
      }
      if (node.nodeName === 'hardBreak') {
        out.push('\n')
        return
      }
      if (node.nodeName === 'calc') {
        out.push(`${String(node.getAttribute('name') ?? '')} = ${displayText(String(node.getAttribute('expr') ?? ''))}\n`)
        return
      }
      for (const c of node.toArray()) walk(c as Y.XmlElement | Y.XmlText)
      if (BLOCK_NODES.has(node.nodeName)) out.push('\n')
      return
    }
    if (node instanceof Y.XmlFragment) for (const c of node.toArray()) walk(c as Y.XmlElement | Y.XmlText)
  }
  walk(frag)
  return out
    .join('')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

function str(v: unknown): string | undefined {
  return typeof v === 'string' && v !== '' ? v : undefined
}
function num(v: unknown): number | undefined {
  return typeof v === 'number' && Number.isFinite(v) ? v : undefined
}

/** Cheap subset for tree resolution. */
export function readTreeNode(id: string, y: YItem): TreeNodeInput {
  const parentId = y.get('parentId')
  return {
    id,
    parentId: typeof parentId === 'string' && parentId ? parentId : null,
    order: (y.get('order') as string) ?? 'a0',
    updatedAt: num(y.get('updatedAt')) ?? 0,
    archivedAt: num(y.get('archivedAt')),
    trashedAt: num(y.get('trashedAt')),
    system: str(y.get('system')),
  }
}

export function readItem(id: string, y: YItem, opts: { bodyText?: boolean } = {}): ItemSnapshot {
  const parentId = y.get('parentId')
  const done = y.get('done')
  const tagsY = yTags(y)
  const propsY = yProps(y)
  const attY = yAttachments(y)
  const compY = yCompletions(y)
  const snap: ItemSnapshot = {
    id,
    parentId: typeof parentId === 'string' && parentId ? parentId : null,
    order: (y.get('order') as string) ?? 'a0',
    title: readTitle(y),
    bodyText: opts.bodyText === false ? '' : fragmentText(yBody(y)),
    done: done === true ? true : done === false ? false : null,
    completions: compY ? compY.toArray() : [],
    tags: tagsY ? [...tagsY.keys()].sort() : [],
    props: propsY ? (propsY.toJSON() as Record<string, unknown>) : {},
    attachments: attY ? [...attY.values()].sort((a, b) => a.addedAt - b.addedAt) : [],
    createdBy: (y.get('createdBy') as ActorId) ?? 'system',
    updatedBy: (y.get('updatedBy') as ActorId) ?? 'system',
    createdAt: num(y.get('createdAt')) ?? 0,
    updatedAt: num(y.get('updatedAt')) ?? 0,
  }
  const opt = <K extends keyof ItemSnapshot>(k: K, v: ItemSnapshot[K] | undefined) => {
    if (v !== undefined) snap[k] = v
  }
  opt('dayOrder', str(y.get('dayOrder')))
  opt('completedAt', num(y.get('completedAt')))
  opt('completedBy', str(y.get('completedBy')))
  opt('date', str(y.get('date')))
  opt('time', str(y.get('time')))
  opt('endTime', str(y.get('endTime')))
  const recur = y.get('recur')
  if (recur && typeof recur === 'object') snap.recur = recur as Recurrence
  const remind = y.get('remind')
  if (Array.isArray(remind) && remind.length) snap.remind = remind as Reminder[]
  const assignee = y.get('assignee')
  if (typeof assignee === 'string' && assignee) snap.assignee = assignee
  const link = y.get('link')
  if (link && typeof link === 'object') snap.link = link as LinkPreview
  const cf = y.get('childFields')
  if (Array.isArray(cf) && cf.length) snap.childFields = cf as string[]
  const rf = y.get('rowFields')
  if (Array.isArray(rf)) snap.rowFields = rf.filter((k): k is string => typeof k === 'string')
  for (const k of ['pageOrder', 'childPageOrder'] as const) {
    const po = y.get(k)
    if (Array.isArray(po) && po.length) snap[k] = normalizePageOrder(po)
  }
  const labels = y.get('labels')
  if (labels && typeof labels === 'object') snap.labels = labels as Record<string, string>
  if (y.get('pinned') === true) snap.pinned = true
  if (y.get('page') === true) snap.page = true
  const ds = y.get('doneStyle')
  if (ds === 'bottom' || ds === 'inline') snap.doneStyle = ds
  opt('tint', str(y.get('tint')) as Tint | undefined)
  opt('icon', cleanIcon(y.get('icon')))
  opt('coverId', str(y.get('coverId')))
  opt('reopensOn', str(y.get('reopensOn')))
  const ci = y.get('calcInside')
  if (ci && typeof ci === 'object') {
    const rules = Object.fromEntries(Object.entries(ci as Record<string, unknown>).filter(([, v]) => typeof v === 'string' && v)) as Record<string, string>
    if (Object.keys(rules).length) snap.calcInside = rules
  }
  opt('total', str(y.get('total')))
  const lines = readCalcLines(yBody(y))
  if (lines.length) snap.calcLines = lines
  const dg = yDiagrams(y)
  if (dg && dg.size) snap.diagrams = dg.toJSON() as Record<string, Diagram>
  const share = y.get('shareToken')
  if (typeof share === 'string' && share) snap.shareToken = share
  opt('archivedAt', num(y.get('archivedAt')))
  opt('trashedAt', num(y.get('trashedAt')))
  opt('system', str(y.get('system')) as SystemKind | undefined)
  return snap
}

/** A note's lines of math (SPEC §7.12): its `calc` blocks, in order. */
export function readCalcLines(frag: Y.XmlFragment | undefined): CalcLine[] {
  const out: CalcLine[] = []
  if (!frag) return out
  for (const n of frag.toArray()) {
    if (!(n instanceof Y.XmlElement) || n.nodeName !== 'calc') continue
    const id = n.getAttribute('id')
    const name = n.getAttribute('name')
    const expr = n.getAttribute('expr')
    if (typeof id === 'string' && id && typeof expr === 'string') out.push({ id, name: typeof name === 'string' ? name : '', expr })
  }
  return out
}

export function readAllItems(doc: Y.Doc, opts?: { bodyText?: boolean }): Map<string, ItemSnapshot> {
  const out = new Map<string, ItemSnapshot>()
  itemsMap(doc).forEach((y, id) => {
    if (y instanceof Y.Map) out.set(id, readItem(id, y, opts))
  })
  return out
}

// ---------------------------------------------------------------------------
// Writing (call inside doc.transact)

export interface ItemInit {
  id?: string
  parentId?: string | null
  order?: string
  dayOrder?: string
  title?: string
  done?: boolean | null
  date?: string
  time?: string
  endTime?: string
  recur?: Recurrence
  remind?: Reminder[]
  assignee?: UserId | null
  tags?: string[]
  props?: Record<string, unknown>
  link?: LinkPreview
  childFields?: string[]
  labels?: Record<string, string>
  pinned?: boolean
  page?: boolean
  tint?: Tint
  icon?: string
  system?: SystemKind
  createdAt?: number
}

export function createYItem(doc: Y.Doc, init: ItemInit, actor: ActorId, now: number = Date.now()): YItem {
  const id = init.id ?? ulid(now)
  const y = new Y.Map<unknown>()
  // Integrate first so nested types can be populated.
  itemsMap(doc).set(id, y)
  y.set('id', id)
  y.set('parentId', init.parentId ?? null)
  y.set('order', init.order ?? 'a0')
  if (init.dayOrder) y.set('dayOrder', init.dayOrder)
  const title = new Y.Text()
  y.set('title', title)
  if (init.title) title.insert(0, init.title)
  y.set('body', new Y.XmlFragment())
  y.set('done', init.done === undefined ? false : init.done)
  y.set('completions', new Y.Array<Completion>())
  const tags = new Y.Map<true>()
  y.set('tags', tags)
  for (const t of init.tags ?? []) {
    const n = normalizeTag(t)
    if (n) tags.set(n, true)
  }
  const props = new Y.Map<unknown>()
  y.set('props', props)
  for (const [k, v] of Object.entries(init.props ?? {})) if (v !== undefined && v !== null) props.set(k, v)
  y.set('attachments', new Y.Map<Attachment>())
  const scalars: Array<[string, unknown]> = [
    ['date', init.date],
    ['time', init.time],
    ['endTime', init.endTime],
    ['recur', init.recur],
    ['remind', init.remind],
    ['assignee', init.assignee ?? undefined],
    ['link', init.link],
    ['childFields', init.childFields],
    ['labels', init.labels],
    ['pinned', init.pinned || undefined],
    ['page', init.page || undefined],
    ['tint', init.tint && init.tint !== 'none' ? init.tint : undefined],
    ['icon', cleanIcon(init.icon)],
    ['system', init.system],
  ]
  for (const [k, v] of scalars) if (v !== undefined) y.set(k, v)
  y.set('createdBy', actor)
  y.set('updatedBy', actor)
  y.set('createdAt', init.createdAt ?? now)
  y.set('updatedAt', now)
  return y
}

export function touch(y: YItem, actor: ActorId, now: number = Date.now()) {
  if (y.get('updatedBy') !== actor) y.set('updatedBy', actor)
  y.set('updatedAt', now)
}

/** Set a scalar/JSON key; `undefined` or `null` (for optional keys) deletes it. */
export function setKey(y: YItem, key: string, value: unknown) {
  if (value === undefined) {
    if (y.has(key)) y.delete(key)
    return
  }
  const cur = y.get(key)
  if (cur === value) return
  if (value !== null && typeof value === 'object' && JSON.stringify(cur) === JSON.stringify(value)) return
  y.set(key, value)
}

function isHighSurrogate(c: number) {
  return c >= 0xd800 && c <= 0xdbff
}

/** Minimal-diff update of a Y.Text (keeps concurrent edits elsewhere in the string). */
export function applyTextDiff(ytext: Y.Text, next: string) {
  const prev = ytext.toString()
  if (prev === next) return
  let start = 0
  const minLen = Math.min(prev.length, next.length)
  while (start < minLen && prev.charCodeAt(start) === next.charCodeAt(start)) start++
  // Don't split a surrogate pair.
  if (start > 0 && isHighSurrogate(prev.charCodeAt(start - 1))) start--
  let endPrev = prev.length
  let endNext = next.length
  while (endPrev > start && endNext > start && prev.charCodeAt(endPrev - 1) === next.charCodeAt(endNext - 1)) {
    endPrev--
    endNext--
  }
  if (endPrev < prev.length && endPrev > start && isHighSurrogate(prev.charCodeAt(endPrev - 1))) {
    endPrev++
    endNext++
  }
  if (endPrev > start) ytext.delete(start, endPrev - start)
  if (endNext > start) ytext.insert(start, next.slice(start, endNext))
}

export function ensureTitle(y: YItem): Y.Text {
  let t = yTitle(y)
  if (!t) {
    t = new Y.Text()
    y.set('title', t)
  }
  return t
}
export function ensureBody(y: YItem): Y.XmlFragment {
  let b = yBody(y)
  if (!b) {
    b = new Y.XmlFragment()
    y.set('body', b)
  }
  return b
}
export function ensureTags(y: YItem): Y.Map<true> {
  let t = yTags(y)
  if (!t) {
    t = new Y.Map<true>()
    y.set('tags', t)
  }
  return t
}
export function ensureProps(y: YItem): Y.Map<unknown> {
  let p = yProps(y)
  if (!p) {
    p = new Y.Map<unknown>()
    y.set('props', p)
  }
  return p
}
export function ensureAttachments(y: YItem): Y.Map<Attachment> {
  let a = yAttachments(y)
  if (!a) {
    a = new Y.Map<Attachment>()
    y.set('attachments', a)
  }
  return a
}
export function ensureDiagrams(y: YItem): Y.Map<Diagram> {
  let d = yDiagrams(y)
  if (!d) {
    d = new Y.Map<Diagram>()
    y.set('diagrams', d)
  }
  return d
}
export function ensureCompletions(y: YItem): Y.Array<Completion> {
  let c = yCompletions(y)
  if (!c) {
    c = new Y.Array<Completion>()
    y.set('completions', c)
  }
  return c
}

/** Lowercase flat strings with optional `/` namespaces (SPEC §5.4). */
export function normalizeTag(tag: string): string {
  return tag
    .trim()
    .replace(/^#/, '')
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^a-z0-9/_\-.]/g, '')
    .replace(/\/{2,}/g, '/')
    .replace(/^\/|\/$/g, '')
}

export function setTags(y: YItem, tags: string[]) {
  const m = ensureTags(y)
  const next = new Set(tags.map(normalizeTag).filter(Boolean))
  for (const k of [...m.keys()]) if (!next.has(k)) m.delete(k)
  for (const t of next) if (!m.has(t)) m.set(t, true)
}

export function setProp(y: YItem, key: string, value: unknown) {
  const p = ensureProps(y)
  if (value === undefined || value === null || value === '') {
    if (p.has(key)) p.delete(key)
    return
  }
  if (JSON.stringify(p.get(key)) === JSON.stringify(value)) return
  p.set(key, value)
}

// ---------------------------------------------------------------------------
// Settings

/** Household settings with defaults; values are validated so a bad client can't wedge the server. */
export function readHouseholdSettings(doc: Y.Doc): HouseholdSettings {
  const m = settingsMap(doc)
  const out = { ...HOUSEHOLD_DEFAULTS } as Record<string, unknown>
  m.forEach((v, k) => {
    if (k in HOUSEHOLD_DEFAULTS && v !== undefined && v !== null && typeof v === typeof (HOUSEHOLD_DEFAULTS as unknown as Record<string, unknown>)[k]) out[k] = v
  })
  const s = out as unknown as HouseholdSettings
  const clamp = (n: number, lo: number, hi: number, d: number) => (Number.isFinite(n) ? Math.min(hi, Math.max(lo, Math.round(n))) : d)
  if (!isValidTimeZone(s.timezone)) s.timezone = HOUSEHOLD_DEFAULTS.timezone
  s.leftoversLookback = clamp(s.leftoversLookback, 1, 60, HOUSEHOLD_DEFAULTS.leftoversLookback)
  s.daysHorizon = clamp(s.daysHorizon, 7, 90, HOUSEHOLD_DEFAULTS.daysHorizon)
  s.trashRetentionDays = clamp(s.trashRetentionDays, 1, 365, HOUSEHOLD_DEFAULTS.trashRetentionDays)
  if (![0, 1, 3, 7, 14, 30].includes(s.autoArchiveDays)) s.autoArchiveDays = 0
  if (s.overdue !== 'stay' && s.overdue !== 'rollToToday') s.overdue = 'stay'
  if (!['sat', 'sun', 'mon'].includes(s.nextWeekend)) s.nextWeekend = 'sat'
  if (s.emptyDays !== 'line' && s.emptyDays !== 'hidden') s.emptyDays = 'line'
  if (s.weekStart !== 0 && s.weekStart !== 1) s.weekStart = HOUSEHOLD_DEFAULTS.weekStart
  const placed = s.detailPlacement && typeof s.detailPlacement === 'object' && !Array.isArray(s.detailPlacement) ? s.detailPlacement : {}
  s.detailPlacement = Object.fromEntries(Object.entries(placed).filter(([, v]) => v === 'line' || v === 'grid'))
  s.doneCharts = Array.isArray(s.doneCharts) ? s.doneCharts.filter(isChartSpec) : null
  const seen = s.doneChartIdeas && typeof s.doneChartIdeas === 'object' && !Array.isArray(s.doneChartIdeas) ? s.doneChartIdeas : {}
  s.doneChartIdeas = Object.fromEntries(Object.entries(seen).filter(([, n]) => typeof n === 'number'))
  s.calendarsHidden = Array.isArray(s.calendarsHidden) ? s.calendarsHidden.filter((x): x is string => typeof x === 'string') : []
  if (s.daysLayout !== 'list' && s.daysLayout !== 'month') s.daysLayout = 'list'
  return s
}

export function setHouseholdSetting<K extends keyof HouseholdSettings>(doc: Y.Doc, key: K, value: HouseholdSettings[K]) {
  settingsMap(doc).set(key, value)
}

export function readUserSettings(doc: Y.Doc, userId: UserId): UserSettings & Record<string, unknown> {
  const m = userSettingsMap(doc).get(userId)
  const out: Record<string, unknown> = { ...USER_DEFAULTS }
  if (m instanceof Y.Map)
    m.forEach((v, k) => {
      if (v !== undefined && v !== null) out[k] = v
    })
  return out as UserSettings & Record<string, unknown>
}

/**
 * The time zone a person is in: where their phone last was, else the household's (home). Their
 * "today", date-only reminders, quiet hours and Google Calendar use it.
 */
export function userZone(doc: Y.Doc, userId: UserId): string {
  const tz = readUserSettings(doc, userId).timezone
  return tz && isValidTimeZone(tz) ? tz : readHouseholdSettings(doc).timezone
}

export function ensureUserSettings(doc: Y.Doc, userId: UserId): Y.Map<unknown> {
  const all = userSettingsMap(doc)
  let m = all.get(userId)
  if (!(m instanceof Y.Map)) {
    m = new Y.Map<unknown>()
    all.set(userId, m)
  }
  return m
}

export function setUserSetting(doc: Y.Doc, userId: UserId, key: string, value: unknown) {
  const m = ensureUserSettings(doc, userId)
  if (value === undefined) m.delete(key)
  else m.set(key, value)
}

export function readUsers(doc: Y.Doc): Map<UserId, UserInfo> {
  const out = new Map<UserId, UserInfo>()
  usersMap(doc).forEach((v, k) => out.set(k, v))
  return out
}

export function readFields(doc: Y.Doc): Map<string, FieldDef> {
  const out = new Map<string, FieldDef>()
  fieldsMap(doc).forEach((v, k) => out.set(k, v))
  return out
}

/**
 * An item icon: one emoji (or other single symbol) — the first grapheme of what was typed or
 * pasted, so "🏝️ trip" or a family emoji keeps just the one picture. Anything else → none.
 */
export function cleanIcon(v: unknown): string | undefined {
  if (typeof v !== 'string') return undefined
  const s = v.trim()
  if (!s) return undefined
  const Seg = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: 'grapheme' }) => { segment(s: string): Iterable<{ segment: string }> } }).Segmenter
  const first = Seg ? ([...new Seg(undefined, { granularity: 'grapheme' }).segment(s)][0]?.segment ?? '') : ([...s][0] ?? '')
  if (!first || first.length > 16 || /^[\p{L}\p{N}\p{P}\s]$/u.test(first)) return undefined
  return first
}
